import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const CustomSelect = ({
  allOptions,
  popularOptions,
  value,
  onChange,
  placeholder,
  noOptionsMessage,
  // styles prop ignored — kept for API compatibility
  styles: _styles,
  ...rest
}) => {
  const [inputText, setInputText] = useState(value || '');
  const [isSelected, setIsSelected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeout = useRef(null);

  // Sync input when value changes externally
  useEffect(() => {
    setInputText(value || '');
    setIsSelected(!!value);
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!inputText || inputText.length < 2) {
      return popularOptions;
    }
    return allOptions.filter(opt =>
      opt.label.toLowerCase().includes(inputText.toLowerCase())
    );
  }, [inputText, allOptions, popularOptions]);

  const handleInputChange = useCallback((e) => {
    const text = e.target.value;
    setInputText(text);
    setIsSelected(false);
    setHighlightedIndex(-1);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setIsOpen(true);
    }, 150);

    if (!text) {
      onChange('');
    }
  }, [onChange]);

  const handleSelect = useCallback((option) => {
    setInputText(option.label);
    setIsSelected(true);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(option.value);
  }, [onChange]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setInputText('');
    setIsSelected(false);
    setIsOpen(false);
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback((e) => {
    // Delay to allow click on option to fire first
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        // If user typed but didn't select — keep text but clear value
        if (!isSelected && inputText && inputText !== value) {
          setInputText(value || '');
        }
      }
    }, 150);
  }, [isSelected, inputText, value]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setIsOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredOptions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, [isOpen, highlightedIndex, filteredOptions, handleSelect]);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  const getNoOptionsText = () => {
    if (noOptionsMessage) {
      return noOptionsMessage({ inputValue: inputText });
    }
    if (!inputText || inputText.length === 0) return 'Начните вводить...';
    if (inputText.length === 1) return 'Введите ещё один символ';
    return 'Ничего не найдено';
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      {...rest}
    >
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          value={inputText}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          style={{
            paddingRight: inputText ? '2rem' : undefined,
            borderColor: isSelected ? undefined : undefined,
          }}
        />
        {inputText && (
          <button
            type="button"
            onMouseDown={handleClear}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#aaa',
              padding: '0 2px',
              lineHeight: 1,
              fontSize: '1rem',
            }}
            tabIndex={-1}
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            margin: 0,
            padding: 0,
            listStyle: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {filteredOptions.length === 0 ? (
            <li style={{ padding: '8px 12px', color: '#999', fontSize: '0.9rem' }}>
              {getNoOptionsText()}
            </li>
          ) : (
            filteredOptions.map((opt, idx) => (
              <li
                key={opt.value}
                onMouseDown={() => handleSelect(opt)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  background: idx === highlightedIndex ? 'var(--main-color-light, #e8f0fe)' : 'transparent',
                  fontSize: '0.9rem',
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
